import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  getAufgaben, createAufgabe, updateAufgabe, deleteAufgabe, toggleAufgabe,
  getAufgabenPakete, paketAnwenden,
  CrmAufgabe, AufgabenPaket, AufgabenPaketAufgabe
} from '../lib/crm-api';
import { getProjects } from '../lib/api';
import { supabase } from '../lib/supabase';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Select from '../components/ui/Select';
import Modal from '../components/ui/Modal';
import toast from 'react-hot-toast';

// LocalStorage key für expanded state
const EXPANDED_STATE_KEY = 'todos-expanded-state';

export default function NewTodosPage() {
  const queryClient = useQueryClient();
  const [showTaskForm, setShowTaskForm] = useState(false);
  const [showPaketModal, setShowPaketModal] = useState(false);
  const [showPaketeManager, setShowPaketeManager] = useState(false);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [filterCompleted, setFilterCompleted] = useState<'open' | 'all' | 'completed'>('all');
  const [editingTask, setEditingTask] = useState<CrmAufgabe | null>(null);
  
  // Expanded state für Projekte - wird in localStorage gespeichert
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() => {
    try {
      const saved = localStorage.getItem(EXPANDED_STATE_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  });

  // Speichere expanded state in localStorage
  useEffect(() => {
    localStorage.setItem(EXPANDED_STATE_KEY, JSON.stringify(expandedSections));
  }, [expandedSections]);

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  // Data fetching
  const { data: aufgaben = [], isLoading: loadingAufgaben } = useQuery({
    queryKey: ['crm-aufgaben'],
    queryFn: () => getAufgaben(),
  });

  const { data: projects = [], isLoading: loadingProjects } = useQuery({
    queryKey: ['projects'],
    queryFn: () => getProjects(),
  });

  const { data: pakete = [] } = useQuery({
    queryKey: ['crm-pakete'],
    queryFn: getAufgabenPakete,
  });

  const toggleMutation = useMutation({
    mutationFn: ({ id, erledigt }: { id: string; erledigt: boolean }) => toggleAufgabe(id, erledigt),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['crm-aufgaben'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteAufgabe,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-aufgaben'] });
      toast.success('Aufgabe gelöscht');
    },
  });

  const heute = new Date().toISOString().split('T')[0];

  // Group by project (UNFILTERED for correct progress calculation)
  const aufgabenByProject: Record<string, CrmAufgabe[]> = {};
  
  // First, add all projects
  projects.forEach(project => {
    aufgabenByProject[project.id] = [];
  });

  // Add a special group for tasks without project (UNFILTERED)
  const aufgabenOhneProjekt: CrmAufgabe[] = [];

  // Then add tasks to projects or to "ohne projekt" (USE ALL TASKS)
  aufgaben.forEach(aufgabe => {
    if (aufgabe.project_id && aufgabenByProject[aufgabe.project_id]) {
      aufgabenByProject[aufgabe.project_id].push(aufgabe);
    } else if (!aufgabe.project_id) {
      aufgabenOhneProjekt.push(aufgabe);
    }
  });

  // THEN apply filter for display
  let filteredAufgaben = aufgaben;
  if (filterCompleted === 'open') filteredAufgaben = aufgaben.filter(a => !a.erledigt);
  if (filterCompleted === 'completed') filteredAufgaben = aufgaben.filter(a => a.erledigt);

  // Filtered groups for display
  const filteredAufgabenByProject: Record<string, CrmAufgabe[]> = {};
  projects.forEach(project => {
    filteredAufgabenByProject[project.id] = [];
  });

  const filteredAufgabenOhneProjekt: CrmAufgabe[] = [];

  filteredAufgaben.forEach(aufgabe => {
    if (aufgabe.project_id && filteredAufgabenByProject[aufgabe.project_id]) {
      filteredAufgabenByProject[aufgabe.project_id].push(aufgabe);
    } else if (!aufgabe.project_id) {
      filteredAufgabenOhneProjekt.push(aufgabe);
    }
  });

  // Stats
  const offeneAufgaben = aufgaben.filter(a => !a.erledigt);
  const faelligeAufgaben = offeneAufgaben.filter(a => a.deadline && a.deadline <= heute);
  const erledigteAufgaben = aufgaben.filter(a => a.erledigt);

  if (loadingAufgaben || loadingProjects) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 border-t-gray-900" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">To-Do's</h1>
          <p className="text-sm text-gray-500 mt-0.5">Aufgabenverwaltung für Projekte</p>
        </div>
        <div className="flex gap-2">
          <Button variant="secondary" onClick={() => setShowPaketeManager(true)}>
            Pakete verwalten
          </Button>
          <Button variant="secondary" onClick={() => setShowPaketModal(true)}>
            Paket zuweisen
          </Button>
          <Button onClick={() => setShowTaskForm(true)}>+ Neue Aufgabe</Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-4 gap-3">
        <div 
          onClick={() => setFilterCompleted('open')}
          className={`bg-white border rounded-lg p-3 cursor-pointer transition ${
            filterCompleted === 'open' ? 'border-blue-600 ring-1 ring-blue-600' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <p className="text-xs text-gray-500 uppercase font-medium">Offen</p>
          <p className="text-xl font-semibold text-blue-600">{offeneAufgaben.length}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <p className="text-xs text-gray-500 uppercase font-medium">Fällig</p>
          <p className={`text-xl font-semibold ${faelligeAufgaben.length > 0 ? 'text-red-600' : 'text-gray-400'}`}>
            {faelligeAufgaben.length}
          </p>
        </div>
        <div 
          onClick={() => setFilterCompleted('completed')}
          className={`bg-white border rounded-lg p-3 cursor-pointer transition ${
            filterCompleted === 'completed' ? 'border-green-600 ring-1 ring-green-600' : 'border-gray-200 hover:border-gray-300'
          }`}
        >
          <p className="text-xs text-gray-500 uppercase font-medium">Erledigt</p>
          <p className="text-xl font-semibold text-green-600">{erledigteAufgaben.length}</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <p className="text-xs text-gray-500 uppercase font-medium">Projekte</p>
          <p className="text-xl font-semibold text-gray-900">{projects.length}</p>
        </div>
      </div>

      {/* Filter Bar */}
      {filterCompleted !== 'open' && (
        <div className="flex gap-2">
          <button
            onClick={() => setFilterCompleted('open')}
            className="text-sm text-gray-600 hover:text-gray-900"
          >
            ← Zurück zu offenen Aufgaben
          </button>
        </div>
      )}

      {/* Alert: Fällige Aufgaben */}
      {faelligeAufgaben.length > 0 && filterCompleted === 'open' && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <div className="w-2 h-2 bg-red-600 rounded-full" />
            </div>
            <div>
              <p className="font-medium text-red-900 text-sm">
                {faelligeAufgaben.length} {faelligeAufgaben.length === 1 ? 'Aufgabe ist' : 'Aufgaben sind'} überfällig
              </p>
              <p className="text-xs text-red-700 mt-1">Bitte priorisieren Sie diese Aufgaben</p>
            </div>
          </div>
        </div>
      )}

      {/* Tasks List */}
      <div className="space-y-3">
        {projects.length === 0 && filteredAufgabenOhneProjekt.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg p-12 text-center">
            <p className="text-gray-500 text-sm">Noch keine Aufgaben vorhanden</p>
            <p className="text-xs text-gray-400 mt-1">Erstellen Sie eine neue Aufgabe oder legen Sie ein Projekt an</p>
          </div>
        ) : (
          <>
            {/* Allgemeine Aufgaben (ohne Projekt) */}
            {filteredAufgabenOhneProjekt.length > 0 && (
              <GeneralTasksSection
                aufgaben={filteredAufgabenOhneProjekt}
                expanded={!!expandedSections['general']}
                onToggleExpanded={() => toggleSection('general')}
                onToggleTask={(id, erledigt) => toggleMutation.mutate({ id, erledigt })}
                onDeleteTask={(id) => {
                  if (confirm('Aufgabe löschen?')) deleteMutation.mutate(id);
                }}
                onEditTask={(aufgabe) => setEditingTask(aufgabe)}
                onAddTask={() => {
                  setSelectedProjectId(null);
                  setShowTaskForm(true);
                }}
              />
            )}

            {/* Projekt-basierte Aufgaben - Gruppiert nach Kunden */}
            {(() => {
              // Gruppiere Projekte nach Kunden
              const projectsByCustomer: Record<string, any[]> = {};
              
              projects.forEach(project => {
                const customerId = project.customer_id || 'ohne';
                if (!projectsByCustomer[customerId]) {
                  projectsByCustomer[customerId] = [];
                }
                projectsByCustomer[customerId].push(project);
              });

              // Sortiere Kunden alphabetisch
              const sortedCustomerIds = Object.keys(projectsByCustomer).sort((a, b) => {
                if (a === 'ohne') return 1;
                if (b === 'ohne') return -1;
                const nameA = projectsByCustomer[a][0]?.customer?.name || '';
                const nameB = projectsByCustomer[b][0]?.customer?.name || '';
                return nameA.localeCompare(nameB);
              });

              return sortedCustomerIds.map(customerId => {
                const customerProjects = projectsByCustomer[customerId];
                const customerName = customerProjects[0]?.customer?.name || 'Ohne Kunde';

                return (
                  <div key={customerId} className="space-y-3">
                    {/* Customer Header */}
                    <div className="flex items-center gap-3 mt-6">
                      <div className="flex-1 h-px bg-gray-200" />
                      <h2 className="text-lg font-semibold text-gray-900 px-4">
                        {customerName}
                        <span className="ml-2 text-sm font-normal text-gray-500">
                          ({customerProjects.length} {customerProjects.length === 1 ? 'Projekt' : 'Projekte'})
                        </span>
                      </h2>
                      <div className="flex-1 h-px bg-gray-200" />
                    </div>

                    {/* Projects for this customer */}
                    {customerProjects.map(project => {
                      // Use UNFILTERED for progress calculation
                      const allProjektAufgaben = aufgabenByProject[project.id] || [];
                      const completedCount = allProjektAufgaben.filter(a => a.erledigt).length;
                      const totalCount = allProjektAufgaben.length;
                      const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

                      // Use FILTERED for display
                      const projektAufgaben = filteredAufgabenByProject[project.id] || [];

                      return (
                        <ProjectSection
                          key={project.id}
                          project={project}
                          aufgaben={projektAufgaben}
                          progress={progress}
                          completedCount={completedCount}
                          totalCount={totalCount}
                          expanded={!!expandedSections[project.id]}
                          onToggleExpanded={() => toggleSection(project.id)}
                          onToggleTask={(id, erledigt) => toggleMutation.mutate({ id, erledigt })}
                          onDeleteTask={(id) => {
                            if (confirm('Aufgabe löschen?')) deleteMutation.mutate(id);
                          }}
                          onEditTask={(aufgabe) => setEditingTask(aufgabe)}
                          onAddTask={() => {
                            setSelectedProjectId(project.id);
                            setShowTaskForm(true);
                          }}
                          onApplyPackage={() => {
                            setSelectedProjectId(project.id);
                            setShowPaketModal(true);
                          }}
                        />
                      );
                    })}
                  </div>
                );
              });
            })()}
          </>
        )}
      </div>

      {/* Task Form Modal */}
      {showTaskForm && (
        <Modal 
          isOpen 
          onClose={() => { setShowTaskForm(false); setSelectedProjectId(null); }} 
          title="Neue Aufgabe"
        >
          <TaskForm
            projects={projects}
            preselectedProjectId={selectedProjectId}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['crm-aufgaben'] });
              setShowTaskForm(false);
              setSelectedProjectId(null);
            }}
          />
        </Modal>
      )}

      {/* Paket Modal */}
      {showPaketModal && (
        <Modal 
          isOpen 
          onClose={() => { setShowPaketModal(false); setSelectedProjectId(null); }} 
          title="Aufgabenpaket zuweisen"
        >
          <PaketForm
            pakete={pakete}
            projects={projects}
            preselectedProjectId={selectedProjectId}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['crm-aufgaben'] });
              setShowPaketModal(false);
              setSelectedProjectId(null);
            }}
          />
        </Modal>
      )}

      {/* Pakete Manager Modal */}
      {showPaketeManager && (
        <Modal 
          isOpen 
          onClose={() => setShowPaketeManager(false)} 
          title="Aufgabenpakete verwalten"
          size="xl"
        >
          <PaketeManagerContent queryClient={queryClient} pakete={pakete} />
        </Modal>
      )}

      {/* Edit Task Modal */}
      {editingTask && (
        <Modal 
          isOpen 
          onClose={() => setEditingTask(null)} 
          title="Aufgabe bearbeiten"
        >
          <TaskEditForm
            task={editingTask}
            projects={projects}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['crm-aufgaben'] });
              setEditingTask(null);
              toast.success('Aufgabe aktualisiert');
            }}
            onCancel={() => setEditingTask(null)}
          />
        </Modal>
      )}
    </div>
  );
}

// =============================================
// COMPONENTS
// =============================================

function ProjectSection({ 
  project, 
  aufgaben, 
  progress, 
  completedCount, 
  totalCount,
  expanded,
  onToggleExpanded,
  onToggleTask,
  onDeleteTask,
  onEditTask,
  onAddTask,
  onApplyPackage
}: {
  project: any;
  aufgaben: CrmAufgabe[];
  progress: number;
  completedCount: number;
  totalCount: number;
  expanded: boolean;
  onToggleExpanded: () => void;
  onToggleTask: (id: string, erledigt: boolean) => void;
  onDeleteTask: (id: string) => void;
  onEditTask: (aufgabe: CrmAufgabe) => void;
  onAddTask: () => void;
  onApplyPackage: () => void;
}) {
  const heute = new Date().toISOString().split('T')[0];

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div 
        className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-200 cursor-pointer hover:bg-gray-100 transition"
        onClick={onToggleExpanded}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <svg 
            className={`w-4 h-4 text-gray-400 transition-transform flex-shrink-0 ${expanded ? 'rotate-90' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-gray-900 truncate">{project.name}</h3>
            <p className="text-xs text-gray-500">{project.customer?.name || 'Kein Kunde'}</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-xs text-gray-500">
              {completedCount}/{totalCount} erledigt
            </p>
            {totalCount > 0 && (
              <div className="w-24 h-1.5 bg-gray-200 rounded-full overflow-hidden mt-1">
                <div 
                  className="h-full bg-green-500 transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
          </div>
          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={onApplyPackage}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition"
              title="Paket zuweisen"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
            </button>
            <button
              onClick={onAddTask}
              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-200 rounded transition"
              title="Aufgabe hinzufügen"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Tasks */}
      {expanded && (
        <div className="divide-y divide-gray-100">
          {aufgaben.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-gray-500">Keine Aufgaben vorhanden</p>
              <button
                onClick={onAddTask}
                className="mt-2 text-xs text-blue-600 hover:text-blue-700"
              >
                Erste Aufgabe erstellen
              </button>
            </div>
          ) : (
            aufgaben
              .sort((a, b) => {
                // Sortiere nur nach Deadline, nicht nach erledigt-Status
                if (a.deadline && b.deadline) return a.deadline.localeCompare(b.deadline);
                if (a.deadline && !b.deadline) return -1;
                if (!a.deadline && b.deadline) return 1;
                return 0;
              })
              .map(aufgabe => {
                const istFaellig = aufgabe.deadline && aufgabe.deadline <= heute && !aufgabe.erledigt;
                
                return (
                  <div 
                    key={aufgabe.id}
                    className={`flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition group ${
                      istFaellig ? 'bg-red-50' : ''
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={aufgabe.erledigt}
                      onChange={() => onToggleTask(aufgabe.id, !aufgabe.erledigt)}
                      className={`w-4 h-4 rounded border-2 transition ${
                        aufgabe.erledigt
                          ? 'border-green-500 text-green-500'
                          : istFaellig
                            ? 'border-red-400 text-red-500'
                            : 'border-gray-300 text-blue-600'
                      }`}
                    />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${
                        aufgabe.erledigt 
                          ? 'text-gray-400 line-through' 
                          : 'text-gray-900'
                      }`}>
                        {aufgabe.titel}
                      </p>
                      {aufgabe.beschreibung && (
                        <p className="text-xs text-gray-500 truncate">{aufgabe.beschreibung}</p>
                      )}
                    </div>
                    {aufgabe.deadline && (
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        aufgabe.erledigt
                          ? 'text-gray-400'
                          : istFaellig
                            ? 'bg-red-100 text-red-700 font-medium'
                            : 'text-gray-500'
                      }`}>
                        {new Date(aufgabe.deadline).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                      </span>
                    )}
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => onEditTask(aufgabe)}
                        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-600 transition p-1"
                        title="Bearbeiten"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                      </button>
                      <button
                        onClick={() => onDeleteTask(aufgabe.id)}
                        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition p-1"
                        title="Löschen"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })
          )}
        </div>
      )}
    </div>
  );
}

function GeneralTasksSection({
  aufgaben,
  expanded,
  onToggleExpanded,
  onToggleTask,
  onDeleteTask,
  onEditTask,
  onAddTask,
}: {
  aufgaben: CrmAufgabe[];
  expanded: boolean;
  onToggleExpanded: () => void;
  onToggleTask: (id: string, erledigt: boolean) => void;
  onDeleteTask: (id: string) => void;
  onEditTask: (aufgabe: CrmAufgabe) => void;
  onAddTask: () => void;
}) {
  const heute = new Date().toISOString().split('T')[0];
  const completedCount = aufgaben.filter(a => a.erledigt).length;
  const totalCount = aufgaben.length;
  const progress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
      {/* Header */}
      <div 
        className="flex items-center justify-between px-4 py-3 bg-blue-50 border-b border-blue-200 cursor-pointer hover:bg-blue-100 transition"
        onClick={onToggleExpanded}
      >
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <svg 
            className={`w-4 h-4 text-blue-600 transition-transform flex-shrink-0 ${expanded ? 'rotate-90' : ''}`}
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
          <div className="flex-1 min-w-0">
            <h3 className="font-medium text-gray-900">Allgemeine Aufgaben</h3>
            <p className="text-xs text-gray-600">Nicht projekt-bezogene To-Do's</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-right">
            <p className="text-xs text-gray-600">
              {completedCount}/{totalCount} erledigt
            </p>
            {totalCount > 0 && (
              <div className="w-24 h-1.5 bg-blue-200 rounded-full overflow-hidden mt-1">
                <div 
                  className="h-full bg-blue-600 transition-all duration-500"
                  style={{ width: `${progress}%` }}
                />
              </div>
            )}
          </div>
          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={onAddTask}
              className="p-1.5 text-blue-600 hover:text-blue-700 hover:bg-blue-100 rounded transition"
              title="Aufgabe hinzufügen"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Tasks */}
      {expanded && (
        <div className="divide-y divide-gray-100">
          {aufgaben
          .sort((a, b) => {
            // Sortiere nur nach Deadline, nicht nach erledigt-Status
            if (a.deadline && b.deadline) return a.deadline.localeCompare(b.deadline);
            if (a.deadline && !b.deadline) return -1;
            if (!a.deadline && b.deadline) return 1;
            return 0;
          })
            .map(aufgabe => {
              const istFaellig = aufgabe.deadline && aufgabe.deadline <= heute && !aufgabe.erledigt;
              
              return (
                <div 
                  key={aufgabe.id}
                  className={`flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 transition group ${
                    istFaellig ? 'bg-red-50' : ''
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={aufgabe.erledigt}
                    onChange={() => onToggleTask(aufgabe.id, !aufgabe.erledigt)}
                    className={`w-4 h-4 rounded border-2 transition ${
                      aufgabe.erledigt
                        ? 'border-green-500 text-green-500'
                        : istFaellig
                          ? 'border-red-400 text-red-500'
                          : 'border-gray-300 text-blue-600'
                    }`}
                  />
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${
                      aufgabe.erledigt 
                        ? 'text-gray-400 line-through' 
                        : 'text-gray-900'
                    }`}>
                      {aufgabe.titel}
                    </p>
                    {aufgabe.beschreibung && (
                      <p className="text-xs text-gray-500 truncate">{aufgabe.beschreibung}</p>
                    )}
                  </div>
                  {aufgabe.deadline && (
                    <span className={`text-xs px-2 py-0.5 rounded ${
                      aufgabe.erledigt
                        ? 'text-gray-400'
                        : istFaellig
                          ? 'bg-red-100 text-red-700 font-medium'
                          : 'text-gray-500'
                    }`}>
                      {new Date(aufgabe.deadline).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </span>
                  )}
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => onEditTask(aufgabe)}
                      className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-600 transition p-1"
                      title="Bearbeiten"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => onDeleteTask(aufgabe.id)}
                      className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-600 transition p-1"
                      title="Löschen"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
        </div>
      )}
    </div>
  );
}

function TaskEditForm({ task, projects, onSuccess, onCancel }: {
  task: CrmAufgabe;
  projects: any[];
  onSuccess: () => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState({
    titel: task.titel || '',
    beschreibung: task.beschreibung || '',
    project_id: task.project_id || '',
    deadline: task.deadline || '',
    erledigt: task.erledigt || false,
  });

  const mutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CrmAufgabe> }) => updateAufgabe(id, data),
    onSuccess,
    onError: (err: Error) => toast.error(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const selectedProject = projects.find(p => p.id === form.project_id);
    mutation.mutate({
      id: task.id,
      data: {
        titel: form.titel,
        beschreibung: form.beschreibung || null,
        project_id: form.project_id || null,
        customer_id: selectedProject?.customer_id || null,
        deadline: form.deadline || null,
        erledigt: form.erledigt,
      }
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
      <Input
        label="Aufgabe"
        value={form.titel}
        onChange={(e) => setForm({ ...form, titel: e.target.value })}
        required
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
        <textarea
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          rows={3}
          value={form.beschreibung}
          onChange={(e) => setForm({ ...form, beschreibung: e.target.value })}
        />
      </div>
      <Select
        label="Projekt (optional)"
        value={form.project_id}
        onChange={(e) => setForm({ ...form, project_id: e.target.value })}
        options={[
          { value: '', label: 'Kein Projekt (Allgemeine Aufgabe)' },
          ...projects.map(p => ({ value: p.id, label: `${p.customer?.name || 'Kein Kunde'} - ${p.name}` }))
        ]}
      />
      <Input
        label="Deadline (optional)"
        type="date"
        value={form.deadline}
        onChange={(e) => setForm({ ...form, deadline: e.target.value })}
      />
      <div className="flex items-center">
        <input
          type="checkbox"
          id="edit-erledigt"
          checked={form.erledigt}
          onChange={(e) => setForm({ ...form, erledigt: e.target.checked })}
          className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
        />
        <label htmlFor="edit-erledigt" className="ml-2 text-sm text-gray-700">
          Als erledigt markieren
        </label>
      </div>
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Abbrechen
        </Button>
        <Button type="submit" loading={mutation.isPending}>
          Speichern
        </Button>
      </div>
    </form>
  );
}

function TaskForm({ projects, preselectedProjectId, onSuccess }: {
  projects: any[];
  preselectedProjectId: string | null;
  onSuccess: () => void;
}) {
  const [form, setForm] = useState({
    titel: '',
    beschreibung: '',
    project_id: preselectedProjectId || '',
    deadline: '',
  });

  const mutation = useMutation({
    mutationFn: createAufgabe,
    onSuccess,
    onError: (err: Error) => toast.error(err.message),
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const selectedProject = projects.find(p => p.id === form.project_id);
    mutation.mutate({
      titel: form.titel,
      beschreibung: form.beschreibung || null,
      project_id: form.project_id || null,
      customer_id: selectedProject?.customer_id || null,
      deadline: form.deadline || null,
      erledigt: false,
      prioritaet: null,
      sortierung: 0,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
      <Input
        label="Aufgabe"
        value={form.titel}
        onChange={(e) => setForm({ ...form, titel: e.target.value })}
        required
      />
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
        <textarea
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
          rows={2}
          value={form.beschreibung}
          onChange={(e) => setForm({ ...form, beschreibung: e.target.value })}
        />
      </div>
      <Select
        label="Projekt (optional)"
        value={form.project_id}
        onChange={(e) => setForm({ ...form, project_id: e.target.value })}
        options={[
          { value: '', label: 'Kein Projekt (Allgemeine Aufgabe)' },
          ...projects.map(p => ({ value: p.id, label: `${p.customer?.name || 'Kein Kunde'} - ${p.name}` }))
        ]}
      />
      <Input
        label="Deadline (optional)"
        type="date"
        value={form.deadline}
        onChange={(e) => setForm({ ...form, deadline: e.target.value })}
      />
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="submit" loading={mutation.isPending} disabled={!form.titel}>
          {mutation.isPending ? 'Erstellen...' : 'Erstellen'}
        </Button>
      </div>
    </form>
  );
}

function PaketForm({ pakete, projects, preselectedProjectId, onSuccess }: {
  pakete: AufgabenPaket[];
  projects: any[];
  preselectedProjectId: string | null;
  onSuccess: () => void;
}) {
  const [form, setForm] = useState({
    paketId: '',
    projectId: preselectedProjectId || '',
  });

  const mutation = useMutation({
    mutationFn: ({ paketId, customerId, projectId }: { paketId: string; customerId: string; projectId?: string }) => 
      paketAnwenden(paketId, customerId, projectId),
    onSuccess,
    onError: (err: Error) => toast.error(err.message),
  });

  const selectedPaket = pakete.find(p => p.id === form.paketId);
  const selectedProject = projects.find(p => p.id === form.projectId);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedProject?.customer_id) {
      toast.error('Projekt hat keinen zugewiesenen Kunden');
      return;
    }
    mutation.mutate({
      paketId: form.paketId,
      customerId: selectedProject.customer_id,
      projectId: form.projectId,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-4">
      <Select
        label="Aufgabenpaket"
        value={form.paketId}
        onChange={(e) => setForm({ ...form, paketId: e.target.value })}
        options={[
          { value: '', label: 'Bitte wählen' },
          ...pakete.map(p => ({ value: p.id, label: p.name }))
        ]}
        required
      />
      {selectedPaket && selectedPaket.aufgaben && selectedPaket.aufgaben.length > 0 && (
        <div className="bg-gray-50 rounded-lg p-3">
          <p className="text-xs text-gray-500 uppercase font-medium mb-2">
            Enthaltene Aufgaben ({selectedPaket.aufgaben.length})
          </p>
          <ul className="space-y-1.5">
            {selectedPaket.aufgaben.slice(0, 5).map((aufgabe: AufgabenPaketAufgabe, idx: number) => (
              <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                <span className="w-1 h-1 bg-gray-400 rounded-full mt-1.5 flex-shrink-0" />
                <div className="flex-1">
                  <span>{aufgabe.titel}</span>
                  {aufgabe.tage_bis_deadline && (
                    <span className="ml-2 text-xs text-gray-500">
                      (Fällig in {aufgabe.tage_bis_deadline} {aufgabe.tage_bis_deadline === 1 ? 'Tag' : 'Tagen'})
                    </span>
                  )}
                </div>
              </li>
            ))}
          </ul>
          {selectedPaket.aufgaben.length > 5 && (
            <p className="text-xs text-gray-400 mt-2">
              +{selectedPaket.aufgaben.length - 5} weitere Aufgaben
            </p>
          )}
        </div>
      )}
      <Select
        label="Projekt"
        value={form.projectId}
        onChange={(e) => setForm({ ...form, projectId: e.target.value })}
        options={[
          { value: '', label: 'Bitte wählen' },
          ...projects.map(p => ({ value: p.id, label: `${p.customer?.name || 'Kein Kunde'} - ${p.name}` }))
        ]}
        required
      />
      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="submit" loading={mutation.isPending} disabled={!form.paketId || !form.projectId}>
          {mutation.isPending ? 'Anwenden...' : 'Paket anwenden'}
        </Button>
      </div>
    </form>
  );
}

// =============================================
// PAKETE MANAGER COMPONENT
// =============================================

function PaketeManagerContent({ queryClient, pakete }: { queryClient: any; pakete: AufgabenPaket[] }) {
  const [showForm, setShowForm] = useState(false);
  const [editingPaket, setEditingPaket] = useState<AufgabenPaket | null>(null);

  const createMutation = useMutation({
    mutationFn: async (data: { name: string; beschreibung: string; aufgaben: AufgabenPaketAufgabe[] }) => {
      const { data: result, error } = await supabase
        .from('crm_aufgaben_pakete')
        .insert({
          name: data.name,
          beschreibung: data.beschreibung,
          aufgaben: data.aufgaben,
        })
        .select()
        .single();
      
      if (error) throw error;
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-pakete'] });
      setShowForm(false);
      toast.success('Paket erstellt');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<AufgabenPaket> }) => {
      const { error } = await supabase
        .from('crm_aufgaben_pakete')
        .update(data)
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-pakete'] });
      setEditingPaket(null);
      setShowForm(false);
      toast.success('Paket aktualisiert');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('crm_aufgaben_pakete')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-pakete'] });
      toast.success('Paket gelöscht');
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleCopyPaket = (paket: AufgabenPaket) => {
    // Generiere einen eindeutigen Namen
    let newName = `${paket.name} (Kopie)`;
    let counter = 1;
    
    while (pakete.some(p => p.name === newName)) {
      counter++;
      newName = `${paket.name} (Kopie ${counter})`;
    }

    // Erstelle eine Kopie mit neuem Namen
    const kopie: AufgabenPaket = {
      ...paket,
      id: '', // Wird von Supabase generiert
      name: newName,
    };

    setEditingPaket(kopie);
    setShowForm(true);
  };

  if (showForm) {
    return (
      <PaketFormComponent
        paket={editingPaket}
        pakete={pakete}
        onSubmit={(data) => {
          if (editingPaket && editingPaket.id) {
            updateMutation.mutate({ id: editingPaket.id, data });
          } else {
            createMutation.mutate(data);
          }
        }}
        onCancel={() => { setShowForm(false); setEditingPaket(null); }}
        isLoading={createMutation.isPending || updateMutation.isPending}
      />
    );
  }

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-end">
        <Button onClick={() => { setEditingPaket(null); setShowForm(true); }} size="sm">
          + Neues Paket
        </Button>
      </div>

      {pakete.length === 0 ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 text-center">
          <p className="text-gray-500 text-sm">Noch keine Pakete definiert</p>
          <p className="text-xs text-gray-400 mt-1">Erstellen Sie Ihr erstes Aufgabenpaket</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-h-[60vh] overflow-y-auto">
          {pakete.map(paket => (
            <div key={paket.id} className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold text-gray-900">{paket.name}</h3>
                  {paket.beschreibung && (
                    <p className="text-xs text-gray-500 mt-1">{paket.beschreibung}</p>
                  )}
                </div>
                <span className="ml-2 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded border border-blue-200">
                  {paket.aufgaben?.length || 0} Aufgaben
                </span>
              </div>

              {paket.aufgaben && paket.aufgaben.length > 0 && (
                <div className="mb-3 space-y-1.5">
                  {paket.aufgaben.slice(0, 3).map((aufgabe, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-xs text-gray-600">
                      <span className="text-gray-400 mt-0.5">•</span>
                      <div className="flex-1">
                        <span>{aufgabe.titel}</span>
                        {aufgabe.tage_bis_deadline && (
                          <span className="ml-2 text-gray-400">({aufgabe.tage_bis_deadline}d)</span>
                        )}
                      </div>
                    </div>
                  ))}
                  {paket.aufgaben.length > 3 && (
                    <p className="text-xs text-gray-400 pl-4">+{paket.aufgaben.length - 3} weitere</p>
                  )}
                </div>
              )}

              <div className="flex gap-2 pt-3 border-t border-gray-100">
                <button
                  onClick={() => { setEditingPaket(paket); setShowForm(true); }}
                  className="flex-1 py-1.5 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded transition"
                >
                  Bearbeiten
                </button>
                <button
                  onClick={() => handleCopyPaket(paket)}
                  className="flex-1 py-1.5 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded transition"
                  title="Paket kopieren"
                >
                  Kopieren
                </button>
                <button
                  onClick={() => {
                    if (confirm('Paket wirklich löschen?')) deleteMutation.mutate(paket.id);
                  }}
                  className="flex-1 py-1.5 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition"
                >
                  Löschen
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function PaketFormComponent({
  paket,
  pakete,
  onSubmit,
  onCancel,
  isLoading,
}: {
  paket: AufgabenPaket | null;
  pakete: AufgabenPaket[];
  onSubmit: (data: { name: string; beschreibung: string; aufgaben: AufgabenPaketAufgabe[] }) => void;
  onCancel: () => void;
  isLoading: boolean;
}) {
  const [form, setForm] = useState({
    name: paket?.name || '',
    beschreibung: paket?.beschreibung || '',
    aufgaben: paket?.aufgaben && paket.aufgaben.length > 0 ? paket.aufgaben : [{ titel: '', tage_bis_deadline: null }],
  });

  const handleAddAufgabe = () => {
    setForm({ ...form, aufgaben: [...form.aufgaben, { titel: '', tage_bis_deadline: null }] });
  };

  const handleRemoveAufgabe = (index: number) => {
    setForm({ ...form, aufgaben: form.aufgaben.filter((_, i) => i !== index) });
  };

  const handleAufgabeChange = (index: number, field: 'titel' | 'tage_bis_deadline', value: string | number | null) => {
    const newAufgaben = [...form.aufgaben];
    if (field === 'tage_bis_deadline') {
      newAufgaben[index] = { ...newAufgaben[index], tage_bis_deadline: value as number | null };
    } else {
      newAufgaben[index] = { ...newAufgaben[index], titel: value as string };
    }
    setForm({ ...form, aufgaben: newAufgaben });
  };

  const handleMoveAufgabe = (index: number, direction: 'up' | 'down') => {
    if ((direction === 'up' && index === 0) || (direction === 'down' && index === form.aufgaben.length - 1)) return;
    
    const newAufgaben = [...form.aufgaben];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newAufgaben[index], newAufgaben[targetIndex]] = [newAufgaben[targetIndex], newAufgaben[index]];
    setForm({ ...form, aufgaben: newAufgaben });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validierung: Name darf nicht leer sein
    if (!form.name.trim()) {
      toast.error('Bitte einen Namen eingeben');
      return;
    }

    // Validierung: Name muss eindeutig sein (außer beim Bearbeiten des gleichen Pakets)
    const nameExists = pakete.some(p => 
      p.name.toLowerCase() === form.name.trim().toLowerCase() && 
      p.id !== paket?.id
    );
    
    if (nameExists) {
      toast.error('Ein Paket mit diesem Namen existiert bereits');
      return;
    }

    const cleanedAufgaben = form.aufgaben.filter(a => a.titel.trim() !== '');
    if (cleanedAufgaben.length === 0) {
      toast.error('Bitte mindestens eine Aufgabe hinzufügen');
      return;
    }
    
    onSubmit({
      name: form.name.trim(),
      beschreibung: form.beschreibung.trim(),
      aufgaben: cleanedAufgaben,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 mt-4 max-h-[70vh] overflow-y-auto px-1">
      <Input
        label="Paket-Name"
        value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })}
        placeholder="z.B. Bewerbungspaket, Lead-Generierung"
        required
      />

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Beschreibung</label>
        <textarea
          className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
          rows={2}
          value={form.beschreibung}
          onChange={(e) => setForm({ ...form, beschreibung: e.target.value })}
          placeholder="Kurze Beschreibung des Pakets"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-medium text-gray-700">Aufgaben</label>
          <button
            type="button"
            onClick={handleAddAufgabe}
            className="text-xs text-blue-600 hover:text-blue-700"
          >
            + Aufgabe hinzufügen
          </button>
        </div>
        <div className="space-y-3">
          {form.aufgaben.map((aufgabe, index) => (
            <div key={index} className="flex gap-2 items-start">
              <div className="flex flex-col gap-0.5 pt-2">
                <button
                  type="button"
                  onClick={() => handleMoveAufgabe(index, 'up')}
                  disabled={index === 0}
                  className={`p-0.5 rounded ${index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
                  title="Nach oben"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={() => handleMoveAufgabe(index, 'down')}
                  disabled={index === form.aufgaben.length - 1}
                  className={`p-0.5 rounded ${index === form.aufgaben.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-400 hover:text-gray-700 hover:bg-gray-100'}`}
                  title="Nach unten"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </div>

              <div className="flex-1 space-y-2">
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                  value={aufgabe.titel}
                  onChange={(e) => handleAufgabeChange(index, 'titel', e.target.value)}
                  placeholder={`Aufgabe ${index + 1}`}
                />
                <div className="flex items-center gap-2">
                  <label className="text-xs text-gray-500 w-32">Fällig in (Tagen):</label>
                  <input
                    type="number"
                    min="0"
                    className="w-24 px-2 py-1 border border-gray-200 rounded text-sm focus:outline-none focus:ring-1 focus:ring-gray-400"
                    value={aufgabe.tage_bis_deadline || ''}
                    onChange={(e) => handleAufgabeChange(index, 'tage_bis_deadline', e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="z.B. 7"
                  />
                  <span className="text-xs text-gray-400">Tage nach Start</span>
                </div>
              </div>

              {form.aufgaben.length > 1 && (
                <button
                  type="button"
                  onClick={() => handleRemoveAufgabe(index)}
                  className="px-2 pt-2 text-gray-400 hover:text-red-600 transition"
                  title="Entfernen"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 sticky bottom-0 bg-white">
        <Button type="button" variant="secondary" onClick={onCancel}>
          Abbrechen
        </Button>
        <Button type="submit" disabled={isLoading || !form.name}>
          {isLoading ? 'Speichern...' : paket ? 'Aktualisieren' : 'Erstellen'}
        </Button>
      </div>
    </form>
  );
}
